import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, shows, type Show } from '@/mocks/shows';

function ShowCard({ show }: { show: Show }) {
  return (
    <Link
      to={`/article/${show.id}`}
      className="group block rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all cursor-pointer"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={show.coverImage}
          alt={show.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          title={`${show.title} - 星辰影院热门综艺`}
        />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 flex-wrap">
          {show.isNew && (
            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium">新</span>
          )}
          {show.isHot && (
            <span className="px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium">热</span>
          )}
        </div>
        <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-sm text-background-50 text-xs">
          {show.episodes}期
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base md:text-lg text-foreground-900 mb-1.5 line-clamp-1">{show.title}</h3>
        <p className="text-xs md:text-sm text-foreground-500 mb-3 line-clamp-2">{show.description}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-foreground-400">{show.host}</span>
          <span className="flex items-center gap-1 text-xs text-primary-600 font-medium">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-star-fill text-xs"></i>
            </div>
            {show.rating}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'hot');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug],
  );

  const filteredShows = useMemo(
    () => shows.filter((s) => s.category === activeSlug),
    [activeSlug],
  );

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: activeCategory.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-6">
              {activeCategory.name}
            </h1>

            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredShows.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {filteredShows.map((show) => (
                  <ShowCard key={show.id} show={show} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">
                  该分类暂无内容，去看看其他分类吧
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}